<template>
  <div class="wc-form">
    <validation-observer
      ref="infoRules"
      v-slot="{ invalid }"
      tag="form"
    >
      <b-row>
        <!-- ── Main Certificates ──────────────────────── -->
        <b-col md="6">
          <div class="wc-section-card">
            <div class="wc-section-card__header wc-section-card__header--info">
              <feather-icon
                icon="GridIcon"
                size="15"
                class="mr-50"
              />
              Certificate Groups
            </div>
            <div class="wc-section-card__body">
              <validation-provider
                #default="validationContext"
                name="Certificates"
                rules="required"
              >
                <b-form-checkbox-group
                  id="certificates_id"
                  v-model="engineerForm.certificates"
                  class="wc-checkbox-group"
                  :options="optionsStatusId"
                  :state="getValidationState(validationContext)"
                  multiple
                  @change="uncheckedCertificates"
                />
                <b-form-invalid-feedback>{{ validationContext.errors[0] }}</b-form-invalid-feedback>
              </validation-provider>
            </div>
          </div>
        </b-col>

        <!-- ── Sub Certificates per Group ───────────── -->
        <b-col
          v-for="(group, index) in selectedCertificateGroups"
          :key="index"
          md="6"
          class="mb-1"
        >
          <div class="wc-section-card">
            <div class="wc-section-card__header wc-section-card__header--teal">
              <feather-icon
                icon="ListIcon"
                size="15"
                class="mr-50"
              />
              {{ group.text }}
            </div>
            <div class="wc-section-card__body">
              <validation-provider
                #default="validationContext"
                :name="`subCertificates${index}`"
                rules="required"
              >
                <b-form-checkbox-group
                  :id="`sub_certificates_${index}`"
                  v-model="engineerForm.subCertificates[group.value]"
                  class="wc-checkbox-group"
                  :state="getValidationState(validationContext)"
                  multiple
                >
                  <!-- Select All Toggle -->
                  <div class="wc-select-all">
                    <b-form-checkbox
                      switch
                      class="wc-select-all__toggle"
                      @change="handelValue($event, group)"
                    >
                      <span class="wc-select-all__label">
                        <feather-icon
                          icon="CheckSquareIcon"
                          size="13"
                          class="mr-25"
                        />
                        Select All
                      </span>
                    </b-form-checkbox>
                  </div>

                  <!-- Individual Certificate Items -->
                  <div
                    v-for="certificate in group.certificates"
                    :id="certItemDomId(certificate.value)"
                    :key="certificate.value"
                    class="wc-cert-item"
                    :class="{ 'wc-cert-item--highlighted': highlightedCertId === certificate.value }"
                  >
                    <div class="wc-cert-item__main">
                      <b-form-checkbox
                        :value="certificate.value"
                        class="wc-cert-item__check"
                      >
                        {{ certificate.text }}
                      </b-form-checkbox>
                      <span
                        v-if="certificate.span"
                        class="wc-cert-item__status"
                      >
                        <feather-icon
                          icon="ActivityIcon"
                          size="11"
                          class="mr-25"
                        />
                        {{ certificate.span }}
                      </span>
                    </div>
                    <b-button
                      v-if="isPersistedBackendSubcert(group.value, certificate.value)"
                      size="sm"
                      variant="outline-primary"
                      class="wc-cert-entry-btn"
                      @click.stop="openCertStatusEntry(group, certificate)"
                    >
                      <feather-icon
                        icon="ClipboardIcon"
                        size="13"
                        class="mr-25"
                      />
                      Certificate Status Entry
                    </b-button>
                  </div>
                </b-form-checkbox-group>
                <b-form-invalid-feedback>{{ validationContext.errors[0] }}</b-form-invalid-feedback>
              </validation-provider>
            </div>
          </div>
        </b-col>
      </b-row>

      <!-- Errors -->
      <b-row v-if="Object.values(errors).length > 0">
        <b-col cols="12">
          <p
            v-for="(error, idx) in errors"
            :key="idx"
            class="text-danger small"
          >
            {{ error }}
          </p>
        </b-col>
      </b-row>

      <!-- Save -->
      <b-row class="mt-1">
        <b-col cols="12">
          <b-button
            v-if="!loader"
            variant="primary"
            class="wc-save-btn"
            :disabled="invalid || Object.values(errors).length > 0"
            @click="addUserSite"
          >
            <feather-icon
              icon="SaveIcon"
              size="16"
              class="mr-50"
            />
            Save Changes
          </b-button>
          <b-button
            v-else
            variant="primary"
            disabled
            class="wc-save-btn"
          >
            <b-spinner
              small
              type="grow"
              class="mr-50"
            />
            Saving...
          </b-button>
        </b-col>
      </b-row>
    </validation-observer>

    <!-- Certificate Status Entry Modal -->
    <b-modal
      v-model="certEntryModalShow"
      centered
      size="lg"
      hide-footer
      modal-class="wc-entry-modal"
      content-class="wc-entry-modal__content"
      @hidden="resetCertEntryModal"
    >
      <template #modal-title>
        <div class="wc-entry-modal__title">
          <feather-icon
            icon="ClipboardIcon"
            size="18"
            class="mr-50"
          />
          Certificate Status Entry
        </div>
      </template>

      <div
        v-if="certEntryEntry"
        class="wc-entry-modal__body"
      >
        <div class="wc-entry-modal__section">
          <div class="wc-entry-modal__section-label">
            <feather-icon
              icon="LayersIcon"
              size="14"
              class="mr-50"
            />
            Certificate Group
          </div>
          <p class="wc-entry-display-name">
            {{ certEntryEntry.groupName }}
          </p>
        </div>

        <div class="wc-entry-modal__divider">
          <feather-icon
            icon="ChevronDownIcon"
            size="16"
          />
        </div>

        <div class="wc-entry-modal__section">
          <div class="wc-entry-modal__section-label">
            <feather-icon
              icon="AwardIcon"
              size="14"
              class="mr-50"
            />
            Sub-Certificate
          </div>
          <p class="wc-entry-display-name">
            {{ certEntryEntry.subCertName }}
          </p>
          <div
            v-if="certEntryEntry.subCertNameAr || certEntryEntry.subCertNameEn"
            class="wc-entry-names-row"
          >
            <span
              v-if="certEntryEntry.subCertNameEn"
              class="wc-entry-name-chip"
            >
              EN: {{ certEntryEntry.subCertNameEn }}
            </span>
            <span
              v-if="certEntryEntry.subCertNameAr"
              class="wc-entry-name-chip wc-entry-name-chip--ar"
            >
              AR: {{ certEntryEntry.subCertNameAr }}
            </span>
          </div>
        </div>

        <div class="wc-entry-modal__section wc-entry-modal__section--statuses">
          <div class="wc-entry-modal__section-label">
            <feather-icon
              icon="CheckSquareIcon"
              size="14"
              class="mr-50"
            />
            Status Assignments
          </div>
          <b-row>
            <b-col md="6">
              <label class="wc-entry-cs-label">
                <feather-icon
                  icon="FileIcon"
                  size="12"
                  class="mr-25"
                />
                Certificate Statuses
              </label>
              <b-form-checkbox-group
                v-model="certEntrySelectedCerStatuses"
                class="wc-entry-checkbox-list"
                :options="statusesCer"
                stacked
              />
            </b-col>
            <b-col md="6">
              <label class="wc-entry-cs-label">
                <feather-icon
                  icon="UserCheckIcon"
                  size="12"
                  class="mr-25"
                />
                Engineer Statuses
              </label>
              <b-form-checkbox-group
                v-model="certEntrySelectedEngStatuses"
                class="wc-entry-checkbox-list"
                :options="statusesEng"
                stacked
              />
            </b-col>
          </b-row>
        </div>
      </div>

      <div class="wc-entry-modal__footer">
        <b-button
          variant="outline-secondary"
          class="wc-entry-modal__cancel-btn"
          :disabled="certEntrySaving"
          @click="certEntryModalShow = false"
        >
          Cancel
        </b-button>
        <b-button
          variant="primary"
          class="wc-entry-modal__close-btn"
          :disabled="certEntrySaving || !hasCertEntryStatusesSelected"
          @click="saveCertStatusEntry"
        >
          <b-spinner
            v-if="certEntrySaving"
            small
            type="grow"
            class="mr-50"
          />
          <feather-icon
            v-else
            icon="SaveIcon"
            size="16"
            class="mr-50"
          />
          {{ certEntrySaving ? 'Saving...' : 'Save Changes' }}
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import formValidation from '@core/comp-functions/forms/form-validation'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import decryptData from '@/store/auth/decryptData'

export default {
  props: {
    engineerForm: {
      type: Object,
      required: true,
    },
    allDataCerStatus: {
      type: Object,
      default: () => ({}),
    },
    statusesCer: {
      type: Array,
      default: () => [],
    },
    statusesEng: {
      type: Array,
      default: () => [],
    },
    allCertificates: {
      type: Array,
      required: true,
    },
    certificatesGroups: {
      type: Array,
      default: () => [],
    },
    optionsStatusId: {
      type: Array,
      required: true,
    },
    selectedCertificateGroups: {
      type: Array,
      required: true,
    },
    /** Sub-cert IDs from last GET engineer/:id load; same keys as engineerForm.subCertificates (group value = name.ar) */
    backendWorkSubCertificates: {
      type: Object,
      default: () => ({}),
    },
    fetchDataCertificates: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      loader: false,
      errors: {},
      loaderStatus: false,
      id: this.$store.state.generalIds.id,
      optionsStatus: [],
      optionsAllocationSources: [],
      certEntryModalShow: false,
      certEntryEntry: null,
      certEntrySelectedCerStatuses: [],
      certEntrySelectedEngStatuses: [],
      certEntrySaving: false,
      highlightedCertId: null,
      highlightClearTimer: null,
    }
  },

  computed: {
    hasCertEntryStatusesSelected() {
      return this.certEntrySelectedCerStatuses.length > 0
        || this.certEntrySelectedEngStatuses.length > 0
    },
  },

  setup() {
    const { getValidationState } = formValidation()

    return {
      getValidationState,
    }
  },
  mounted() {
    this.restoreScrollToEditedCertificate()
  },
  beforeDestroy() {
    if (this.highlightClearTimer) {
      clearTimeout(this.highlightClearTimer)
    }
  },
  methods: {
    certItemDomId(certificateId) {
      return `wc-cert-item-${certificateId}`
    },
    rememberEditedCertificate(certificateId) {
      if (certificateId == null) return
      try {
        sessionStorage.setItem('wc-scroll-cert-id', String(certificateId))
      } catch {
        // ignore storage errors
      }
    },
    restoreScrollToEditedCertificate() {
      let certificateId = null
      try {
        certificateId = sessionStorage.getItem('wc-scroll-cert-id')
        if (certificateId) {
          sessionStorage.removeItem('wc-scroll-cert-id')
        }
      } catch {
        return
      }
      if (!certificateId) return

      const numericId = Number(certificateId)
      const targetId = Number.isNaN(numericId) ? certificateId : numericId

      setTimeout(() => {
        this.scrollToEditedCertificate(targetId)
      }, 150)
    },
    scrollToEditedCertificate(certificateId) {
      if (certificateId == null || certificateId === '') return

      this.$nextTick(() => {
        const el = document.getElementById(this.certItemDomId(certificateId))
        if (!el) return

        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        this.highlightedCertId = certificateId

        if (this.highlightClearTimer) {
          clearTimeout(this.highlightClearTimer)
        }
        this.highlightClearTimer = setTimeout(() => {
          this.highlightedCertId = null
          this.highlightClearTimer = null
        }, 3500)
      })
    },
    isPersistedBackendSubcert(groupKey, certificateId) {
      const ids = this.backendWorkSubCertificates[groupKey]
      if (!Array.isArray(ids)) return false
      return ids.some(id => id === certificateId || String(id) === String(certificateId))
    },
    openCertStatusEntry(group, certificate) {
      if (!this.isPersistedBackendSubcert(group.value, certificate.value)) return

      const groupMeta = this.certificatesGroups.find(g => g.text === group.text)
      const rawGroup = this.allCertificates.find(g => g.value === group.value)
      const subCert = groupMeta?.certificates?.find(c => c.id === certificate.value)
        || rawGroup?.certificates?.find(c => c.id === certificate.value)

      const existingStatuses = this.allDataCerStatus[certificate.value] || []

      this.certEntryEntry = {
        groupId: groupMeta?.value ?? '—',
        groupName: group.text,
        subCertId: certificate.value,
        subCertName: certificate.text,
        subCertNameAr: subCert?.name?.ar ?? '',
        subCertNameEn: subCert?.name?.en ?? '',
        statusSummary: certificate.span || '—',
      }
      this.certEntrySelectedCerStatuses = this.statusesCer
        .filter(s => existingStatuses.includes(s.text))
        .map(s => s.value)
      this.certEntrySelectedEngStatuses = this.statusesEng
        .filter(s => existingStatuses.includes(s.text))
        .map(s => s.value)
      this.certEntryModalShow = true
    },
    resetCertEntryModal() {
      this.certEntryEntry = null
      this.certEntrySelectedCerStatuses = []
      this.certEntrySelectedEngStatuses = []
      this.certEntrySaving = false
    },
    buildCertEntryPayload() {
      const userId = this.$route.params.id
      const certId = this.certEntryEntry.subCertId
      const data = []

      this.certEntrySelectedCerStatuses.forEach(statusId => {
        data.push({
          certificate_id: certId,
          status_id: statusId,
          user_id: userId,
        })
      })
      this.certEntrySelectedEngStatuses.forEach(statusId => {
        data.push({
          certificate_id: certId,
          status_id: statusId,
          user_id: userId,
        })
      })

      return { data }
    },
    async saveCertStatusEntry() {
      if (!this.certEntryEntry || !this.hasCertEntryStatusesSelected) return

      const editedCertId = this.certEntryEntry.subCertId
      this.certEntrySaving = true
      try {
        const payload = this.buildCertEntryPayload()
        const res = await axios.post('engineer-certificate-status/assign', payload)

        if (res.status === 200 || res.status === 201) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Updated Successfully',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
          this.certEntryModalShow = false
          this.rememberEditedCertificate(editedCertId)
          await this.fetchDataCertificates()
          // Component remounts after loader; mounted() restores scroll.
          // Also try here in case content stayed mounted.
          await this.$nextTick()
          setTimeout(() => {
            this.scrollToEditedCertificate(editedCertId)
          }, 150)
        }
      } catch (error) {
        if (error.response?.status) {
          const message = error.response.data?.message || 'An error occurred'
          this.$toast({
            component: ToastificationContent,
            props: {
              title: message,
              icon: 'BellIcon',
              variant: 'error',
            },
          })
        }
      } finally {
        this.certEntrySaving = false
      }
    },
    handelValue(value, group) {
      if (value.length === 1) {
        this.engineerForm.subCertificates[group.value] = group.certificates.map(item => item.value)
      } else {
        this.engineerForm.subCertificates[group.value] = []
      }
    },
    uncheckedCertificates(checked) {
      const removedCertificates = Object.keys(this.engineerForm.subCertificates).filter(cert => !checked.includes(cert))

      removedCertificates.forEach(cert => {
        this.$delete(this.engineerForm.subCertificates, cert)
      })

      this.engineerForm.certificates = checked
    },
    addUserSite() {
      this.loader = true
      const formData = new FormData()

      Object.keys(this.engineerForm.subCertificates).forEach(key => {
        this.engineerForm.subCertificates[key].forEach(subCert => {
          formData.append('certificates[]', subCert)
        })
      })

      axios
        .post(`/engineer/${this.$route.params.id}/certificate`, formData)
        .then(res => {
          if (res.status === 200 || res.status === 201) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Updated Successfully',
                icon: 'CheckIcon',
                variant: 'success',
              },
            })
            this.fetchDataCertificates()
          }
        })
        .catch(error => {
          if (error.response.status) {
            const payload = decryptData(error.response.data.payload).message
            const firstArrayKey = Object.keys(payload).find(key => Array.isArray(payload[key]))
            const errorMessage = firstArrayKey ? payload[firstArrayKey][0] : 'An unknown error occurred'
            this.$toast({
              component: ToastificationContent,
              props: {
                title: errorMessage,
                icon: 'BellIcon',
                variant: 'error',
              },
            })
          }
        })
        .finally(() => {
          this.loader = false
        })
    },

  },
}
</script>

<style lang="scss">
@import './certificates-wizard.scss';
</style>
